import { useCallback, useEffect, useRef, useState } from "react";

interface PollState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Refetch immediately — used after bot control actions. */
  refresh: () => void;
}

/**
 * Poll a JSON endpoint on an interval.
 *
 * The bot scans every 5s (bot.scan_interval), so polling faster than that just
 * re-renders identical data. Polling pauses while the tab is hidden and fires
 * once on return, so a backgrounded dashboard doesn't hammer uvicorn.
 */
export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs = 4000,
): PollState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // keep the latest fetcher without making it an effect dependency
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const alive = useRef(true);

  const run = useCallback(async () => {
    try {
      const next = await fetcherRef.current();
      if (!alive.current) return;
      setData(next);
      setError(null);
    } catch (e) {
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    let timer: number | undefined;

    // `force` on the first call: the initial load must happen even in a
    // background tab, or a dashboard opened via cmd-click sits on the loading
    // spinner until it is focused. Only repeat polls pause while hidden.
    const tick = (force = false) => {
      if (force || !document.hidden) void run();
      timer = window.setTimeout(tick, intervalMs);
    };
    tick(true);

    const onVisible = () => {
      if (!document.hidden) void run();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      alive.current = false;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [run, intervalMs]);

  return { data, error, loading, refresh: run };
}
